import React, { useEffect, useRef, useState } from "react";
import { Accessibility, Contrast, Type, Check, X, Moon } from "lucide-react";
import { useAccessibility, QUIET_WINDOW } from "../contexts/AccessibilityContext";
import { useClara } from "../contexts/ClaraContext";

const SCALE_LABEL = { 1: "Normal", 1.25: "Larger", 1.5: "Largest" };

const fmtHour = (h) => {
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12} ${period}`;
};

export const AccessibilityMenu = () => {
    const {
        highContrast,
        textScale,
        quietMode,
        toggleHighContrast,
        cycleTextScale,
        toggleQuietMode,
    } = useAccessibility();
    const { speak } = useClara();
    const [open, setOpen] = useState(false);
    const popoverRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const handleHC = () => {
        toggleHighContrast();
        speak(
            highContrast
                ? "High contrast turned off."
                : "High contrast turned on.",
        );
    };

    const handleScale = () => {
        cycleTextScale();
        const next = textScale === 1 ? 1.25 : textScale === 1.25 ? 1.5 : 1;
        speak(`Text size set to ${SCALE_LABEL[next].toLowerCase()}.`);
    };

    const handleQuiet = () => {
        toggleQuietMode();
        speak(
            quietMode
                ? "Quiet voice turned off. Clara will speak at normal volume."
                : "Quiet voice turned on. Clara will speak softly and a little slower.",
        );
    };

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="Accessibility settings"
                aria-expanded={open}
                aria-haspopup="dialog"
                data-testid="a11y-menu-btn"
                className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-xl bg-white border-2 border-[#0A3D7A] text-[#0A3D7A] hover:bg-[#0A3D7A] hover:text-white transition-colors"
            >
                <Accessibility className="w-5 h-5" strokeWidth={2.3} />
            </button>

            {open && (
                <div
                    ref={popoverRef}
                    role="dialog"
                    aria-label="Accessibility options"
                    data-testid="a11y-menu-popover"
                    className="absolute right-0 top-[calc(100%+8px)] z-40 w-72 bg-white rounded-2xl border border-[#0A3D7A]/15 shadow-2xl p-3 fade-in-up"
                >
                    <div className="flex items-center justify-between px-1 pb-2">
                        <p className="font-display font-bold text-[#1A1A2E]">
                            Accessibility
                        </p>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Close accessibility menu"
                            data-testid="a11y-menu-close"
                            className="w-9 h-9 rounded-lg hover:bg-[#FFF8F0] flex items-center justify-center text-[#5A5A6E]"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="space-y-2">
                        <button
                            type="button"
                            onClick={handleHC}
                            aria-pressed={highContrast}
                            data-testid="a11y-toggle-contrast"
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FFF8F0] border border-[#F0EAD6] hover:border-[#0A3D7A] transition-colors text-left"
                        >
                            <span
                                aria-hidden="true"
                                className="shrink-0 w-9 h-9 rounded-lg bg-[#0A3D7A] flex items-center justify-center text-white"
                            >
                                <Contrast className="w-4 h-4" />
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="block font-semibold text-[#1A1A2E]">
                                    High contrast
                                </span>
                                <span className="block text-xs text-[#5A5A6E]">
                                    Bolder borders, darker text
                                </span>
                            </span>
                            <span
                                className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center border-2 ${
                                    highContrast
                                        ? "bg-[#0A5C36] border-[#0A5C36] text-white"
                                        : "border-[#5A5A6E]/40 text-transparent"
                                }`}
                                aria-hidden="true"
                            >
                                <Check className="w-4 h-4" />
                            </span>
                        </button>

                        <button
                            type="button"
                            onClick={handleScale}
                            data-testid="a11y-toggle-text-size"
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FFF8F0] border border-[#F0EAD6] hover:border-[#0A3D7A] transition-colors text-left"
                        >
                            <span
                                aria-hidden="true"
                                className="shrink-0 w-9 h-9 rounded-lg bg-[#B8860B] flex items-center justify-center text-[#1A1A2E]"
                            >
                                <Type className="w-4 h-4" />
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="block font-semibold text-[#1A1A2E]">
                                    Text size
                                </span>
                                <span className="block text-xs text-[#5A5A6E]">
                                    Tap to cycle Normal → Larger → Largest
                                </span>
                            </span>
                            <span
                                className="shrink-0 inline-flex items-center justify-center rounded-full bg-[#0A3D7A] text-white px-3 py-1 text-xs font-bold uppercase tracking-wider"
                                data-testid="a11y-text-size-label"
                            >
                                {SCALE_LABEL[textScale]}
                            </span>
                        </button>

                        <button
                            type="button"
                            onClick={handleQuiet}
                            aria-pressed={quietMode}
                            data-testid="a11y-toggle-quiet"
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FFF8F0] border border-[#F0EAD6] hover:border-[#0A3D7A] transition-colors text-left"
                        >
                            <span
                                aria-hidden="true"
                                className="shrink-0 w-9 h-9 rounded-lg bg-[#1A1A2E] flex items-center justify-center text-[#E0C875]"
                            >
                                <Moon className="w-4 h-4" />
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="block font-semibold text-[#1A1A2E]">
                                    Quiet voice
                                </span>
                                <span
                                    className="block text-xs text-[#5A5A6E]"
                                    data-testid="a11y-quiet-window-label"
                                >
                                    Softer &amp; slower · suggested {fmtHour(QUIET_WINDOW.start)} – {fmtHour(QUIET_WINDOW.end)}
                                </span>
                            </span>
                            <span
                                className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center border-2 ${
                                    quietMode
                                        ? "bg-[#0A5C36] border-[#0A5C36] text-white"
                                        : "border-[#5A5A6E]/40 text-transparent"
                                }`}
                                aria-hidden="true"
                            >
                                <Check className="w-4 h-4" />
                            </span>
                        </button>
                    </div>
                    <p className="px-1 pt-2 text-[11px] text-[#5A5A6E] leading-snug">
                        Settings save to this device. Browser zoom (Ctrl /
                        Cmd&nbsp;+) is also fully supported up to 200%.
                    </p>
                </div>
            )}
        </div>
    );
};

export default AccessibilityMenu;
